<template>
	<div class="dashboard">
		<b-loading :is-full-page="true" :active.sync="isLoading" :can-cancel="false"></b-loading>
		<h1>Dashboard</h1>
		<div class="content-wrapper">
			<b-field label="Select Shop (optional)">
				<b-autocomplete
					v-model="shopsSuggestions"
					:data="filteredShops"
					:open-on-focus="true"
					placeholder="Shop"
					field="name"
					@select="option => (selectedShop = option)"
					class="shopAutoComplete"
				>
					<template slot="empty">No results found</template>
				</b-autocomplete>
			</b-field>

			<b-field label="Select Brand (optional)">
				<b-autocomplete
					v-model="brandSuggestions"
					:data="filteredBrands"
					:open-on-focus="true"
					placeholder="Brand"
					field="name"
					@select="option => (selectedBrand = option)"
					class="brandAutocomplete"
				>
					<template slot="empty">No results found</template>
				</b-autocomplete>
			</b-field>

			<div class="datepickerWrapper">
				<b-field label="Starting Date">
					<b-datepicker :date-formatter="dateFormatter" placeholder="Click to select..." v-model="startingDate" icon="calendar-today">
						<button class="button is-primary" @click="startingDate = new Date()">
							<b-icon icon="calendar-today"></b-icon>
							<span>Today</span>
						</button>

						<button class="button is-danger" @click="startingDate = null">
							<b-icon icon="close"></b-icon>
							<span>Clear</span>
						</button></b-datepicker
					>
				</b-field>

				<b-field label="Ending Date">
					<b-datepicker
						:date-formatter="dateFormatter"
						placeholder="Click to select..."
						v-model="endingDate"
						icon="calendar-today"
						:min-date="minDate"
						:max-date="maxDate"
					>
						<button class="button is-danger" @click="endingDate = null">
							<b-icon icon="close"></b-icon>
							<span>Clear</span>
						</button></b-datepicker
					>
				</b-field>
			</div>

			<div class="filter-button-wrapper">
				<div class="big-button" @click="submit">SUBMIT</div>
			</div>
		</div>

		<div class="total-sales" v-if="totalSold > 0">
			<div class="total-wrapper">
				<div class="total-amount"><span>Total Sales (Estimated)</span><br />{{ totalSold.toLocaleString('en-GB') }}</div>

				<div class="total-amount"><span>Total Profit (Estimated)</span><br />{{ totalProfit.toLocaleString('en-GB') }}</div>

				<div class="total-orders" v-if="totalOrders > 0"><span>Total Orders</span><br />{{ totalOrders.toLocaleString('en-GB') }}</div>
				<div v-else><span>Total Sold Items</span><br />{{ totalItems.toLocaleString('en-GB') }}</div>
			</div>
		</div>
	</div>
</template>

<script>
export default {
	name: 'Dashboard',
	data: () => ({
		isLoading: false,
		totalSales: [],
		startingDate: null,
		endingDate: null,
		minDate: null,
		maxDate: null,
		shops: [], // For list of shops from API
		shopsSuggestions: '', // For dropdown selection
		selectedShop: null, // Selected shop
		brands: [], // To store brands
		selectedBrand: null,
		brandSuggestions: '',
		totalSold: 0,
		totalProfit: 0,
		totalItems: 0,
		totalOrders: 0
	}),

	watch: {
		startingDate: function(data) {
			if (data !== null) {
				this.minDate = data
				this.maxDate = new Date(data.getFullYear(), data.getMonth(), data.getDate() + 30)

				this.endingDate = data
			} else {
				this.endingDate = null
			}
		},

		// To display or hide brand selection
		topType: function(newData) {
			if (newData == 'products') {
				this.displayBrandSelection = true
			} else {
				this.displayBrandSelection = false
			}
		}
	},

	mounted: async function() {
		await this.getShops()

		await this.getBrands()
	},

	computed: {
		getTotalProfit() {
			const totalProfit = this.totalSales.pay_amount - this.totalSales.original_no_promotion_total_price

			return totalProfit.toLocaleString('en-GB')
		},

		filteredBrands() {
			return this.brands.filter(option => {
				return (
					option.name
						.toString()
						.toLowerCase()
						.indexOf(this.brandSuggestions.toLowerCase()) >= 0
				)
			})
		},

		filteredShops() {
			return this.shops.filter(option => {
				return (
					option.name
						.toString()
						.toLowerCase()
						.indexOf(this.shopsSuggestions.toLowerCase()) >= 0
				)
			})
		}
	},

	methods: {
		// Get all the brands
		getBrands: async function() {
			const data = {
				limit: 1000,
				sort: {
					name: 'asc'
				}
			}
			const apiGetBrands = await this.$api.getBrand({ data })

			if (apiGetBrands !== undefined) {
				this.brands = apiGetBrands.data.list
			}
		},

		// Get list of shops
		getShops: async function() {
			const apiGetShops = await this.$api.getShops({})

			this.shops = apiGetShops.data.list
		},

		submit: async function() {
			if (this.startingDate == null || this.endingDate == null) {
				this.$swal.fire({
					icon: 'error',
					title: 'Oops...',
					text: 'Starting and ending dates are required'
				})

				return
			}

			this.isLoading = true
			this.totalSales = []
			var loop = true
			var loopCount = 0
			this.totalSold = 0
			this.totalProfit = 0
			this.totalItems = 0
			this.totalOrders = 0

			var data = {}
			const createdAt = {}

			if (this.startingDate !== null) {
				createdAt['$gte'] = this.$moment(this.startingDate)
					.set({ hour: 0 })
					.format('x')
			}

			if (this.endingDate !== null) {
				createdAt['$lte'] = this.$moment(this.endingDate)
					.set({ hour: 24 })
					.format('x')
			}

			// For getting total value of sales
			data = {
				pay_status: 1,
				time: {
					createdAt
				}
			}

			if (this.selectedShop && this.selectedShop._id) {
				data.shop = this.selectedShop._id
			}

			if (this.selectedBrand && this.selectedBrand._id) {
				data.order_items = { brand: this.selectedBrand._id }
			} else {
				const getSales = await this.$api.getSales({ data })

				if (getSales && getSales.data[0]) {
					this.totalSold = getSales.data[0].total_price
					this.totalProfit = this.totalSold * 0.25
					this.totalOrders = getSales.data[0].count
				} else {
					this.$swal.fire('No data!', 'No data for selected parameters!', 'info')
				}
				this.isLoading = false
				return
			}

			data.limit = 1000

			while (loop) {
				if (loopCount > 0) {
					data.skip = loopCount * 1000
				}

				const apiGetSales = await this.$api.getTopProducts({ data })

				if (apiGetSales !== undefined && apiGetSales.data.count > 0) {
					apiGetSales.data.list.map(sales => {
						this.totalSales.push(sales)
					})
				}

				if (apiGetSales.data.count == 0) {
					loop = false
					this.$swal.fire('No data!', 'No data for selected parameters!', 'info')
				}

				loop = apiGetSales.data.count < 1000 ? false : true

				loopCount++
			}

			// console.log(this.totalSales.length)

			if (this.totalSales.length > 0) {
				for (const items of this.totalSales) {
					// count_total += items.qty

					// this.totalSales.map(items => {
					for (const item of items.order_items) {
						// items.order_items.map(item => {
						// count_total++

						// console.log(count_total)

						// var price = item.product.price * item.qty
						var price = item.total_price
						var profit = 0

						if (item.product.commission) {
							profit = price * (item.product.commission / 100)
						} else {
							profit = price - item.product.original_price
						}

						this.totalSold += price
						this.totalProfit += profit
						this.totalItems += item.qty
					}
					// )
				}
				// )
			}

			this.isLoading = false
		},

		getSales: async function() {
			const data = {}

			const apiGetSales = await this.$api.getSales({ data })
			if (apiGetSales !== undefined) {
				this.totalSales = apiGetSales.data[0]
			}
		},

		// For calender format
		dateFormatter(dt) {
			return dt.toLocaleDateString('en-GB')
		}
	}
}
</script>

<style scoped>
@import '../styles/GeneralStyles.css';
</style>
